import * as React from 'react';
import { INNER_TABS, type InnerTab } from './inner-tabs.ts';

// The body of a stage tab under its header, drawn from props only: the
// pinned sections right under the header (the opening's book line), the
// inner strip (Overview, Puzzles) when more than one inner tab has content,
// and the active inner tab's sections in a body that scrolls on its own.
// `index.tsx` groups the stage's contributions and passes them in; stories
// pass hand-made sections.

export interface StageBodyProps {
  /** Sections pinned under the header, outside the inner tabs. */
  pinned?: React.ReactNode;
  /** The inner tabs that have content, in strip order. */
  present: InnerTab[];
  /** The inner tab shown; undefined when no inner tab has content. */
  active?: InnerTab;
  /** The active tab's sections. */
  children?: React.ReactNode;
  /** The puzzle count shown on the Puzzles inner tab. */
  badge?: string | number;
  onPick: (tab: InnerTab) => void;
}

export const StageBody: React.FC<StageBodyProps> = ({ pinned, present, active, children, badge, onPick }) => {
  const pinnedBlock = pinned ? <div className="stage-pinned">{pinned}</div> : null;
  if (!active) return pinnedBlock;
  const body = <div className="stage-tab-body panel-grow">{children}</div>;
  if (present.length < 2) {
    return (
      <>
        {pinnedBlock}
        {body}
      </>
    );
  }
  return (
    <>
      {pinnedBlock}
      <nav className="stage-tab-strip" aria-label="Stage">
        {INNER_TABS.filter((t) => present.includes(t.id)).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`stage-tab${t.id === active ? ' stage-tab-active' : ''}`}
            aria-current={t.id === active ? 'true' : undefined}
            data-track="review.tabs.inner"
            data-track-value={t.id}
            onClick={() => onPick(t.id)}>
            {t.title}
            {t.id === 'puzzles' && badge !== undefined && <span className="stage-tab-badge">{badge}</span>}
          </button>
        ))}
      </nav>
      {body}
    </>
  );
};
